import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LOGO_URL } from "../lib/api";
import { Loader2, Lock, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const AdminLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0B2C5B] relative overflow-hidden px-5 py-10"
      data-testid="admin-login-page"
    >
      {/* Wave background */}
      <svg
        className="absolute inset-x-0 bottom-0 w-full opacity-40"
        viewBox="0 0 1440 400"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0,200 C320,320 640,80 960,180 C1200,250 1320,300 1440,220 L1440,400 L0,400 Z"
          fill="#2AB7E8"
          opacity="0.3"
        />
        <path
          d="M0,280 C320,200 640,360 960,240 C1200,170 1320,300 1440,260 L1440,400 L0,400 Z"
          fill="#2AB7E8"
          opacity="0.15"
        />
      </svg>

      <Link to="/" className="absolute top-6 left-6 text-white/70 hover:text-white text-sm inline-flex items-center gap-2" data-testid="admin-back-home">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>

      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-2xl p-3 shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
            <img src={LOGO_URL} alt="Visit Bedford" className="h-24 w-auto block" />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-10 shadow-2xl">
          <div className="text-center mb-7">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#9FD8F0] mb-3">
              Administrator
            </div>
            <h1 className="font-serif-display text-3xl md:text-4xl font-bold text-white leading-tight">
              Welcome back.
            </h1>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div className="flex items-center gap-3 rounded-full px-5 py-4 bg-white/10 border border-white/20">
              <Mail className="w-5 h-5 text-[#9FD8F0]" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@visitbedford.com"
                data-testid="admin-email-input"
                className="flex-1 bg-transparent outline-none text-white placeholder:text-white/40"
              />
            </div>
            <div className="flex items-center gap-3 rounded-full px-5 py-4 bg-white/10 border border-white/20">
              <Lock className="w-5 h-5 text-[#9FD8F0]" />
              <input
                type={showPwd ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="admin-password-input"
                autoComplete="current-password"
                className="flex-1 bg-transparent outline-none text-white placeholder:text-white/40"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                data-testid="admin-toggle-password"
                aria-label={showPwd ? "Hide password" : "Show password"}
                className="text-[#9FD8F0] hover:text-white transition"
              >
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full btn-primary-gradient text-white rounded-full px-7 py-4 font-semibold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-white/50">
            Secure admin access for Visit Bedford team members.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
